import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { saveApps, selectApp } from "../../slices/AppsSlice";
import EntityCard from "../../components/EntityCard";
import { usePageMeta } from "../../contexts/PageMetaContext";
import LoadingSpinner from "../../components/LoadingSpinner";
import useApiCall from "../../hooks/useApiCall";

export default function Clients() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("All");

  useEffect(() => {
    makeRequest("/v1/apps?status=active", { method: "GET", authTokens: tokens })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setApps(list);
        dispatch(saveApps(list));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const FILTERS = [
    { label: "All", value: "All" },
    { label: "Higher Ed", value: "higher_ed" },
    { label: "E-Commerce", value: "ecommerce" },
  ];

  const filteredApps = useMemo(() => {
    if (selectedFilter === "ecommerce") return apps.filter((a) => a.client_type === "ecommerce");
    if (selectedFilter === "higher_ed") return apps.filter((a) => a.client_type === "higher_ed");
    return apps;
  }, [apps, selectedFilter]);

  // Push title metadata into the TopBar
  useEffect(() => {
    setPageMeta({
      description: "Manage clients, business units, and agents",
      actions: (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            component={Link}
            to="/clients/import"
            variant="outlined"
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Import Clients
          </Button>
          <Button
            component={Link}
            to="/clients/new"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Create Client
          </Button>
        </Box>
      ),
    });
    return () => setPageMeta({});
  }, [setPageMeta]);

  const handleAppClick = (app) => {
    dispatch(selectApp(app));
    navigate(`/${app.url_code}/`);
  };

  return (
    <>
      {/* Filter pills */}
      <Box
        sx={{
          mb: 3,
          display: "inline-flex",
          alignItems: "center",
          bgcolor: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          p: "4px",
          gap: "2px",
        }}
      >
        {FILTERS.map(({ label, value }) => {
          const active = selectedFilter === value;
          return (
            <Box
              key={value}
              onClick={() => setSelectedFilter(value)}
              sx={{
                px: "16px",
                py: "6px",
                borderRadius: "8px",
                fontSize: "0.875rem",
                fontWeight: active ? 600 : 400,
                color: active ? "#ffffff" : "#475569",
                bgcolor: active ? "#1e293b" : "transparent",
                cursor: "pointer",
                userSelect: "none",
                transition: "background 0.15s, color 0.15s",
                "&:hover": !active ? { bgcolor: "#f1f5f9", color: "#1e293b" } : {},
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>

      {/* Loading state */}
      {loading && (
        <LoadingSpinner message="Loading clients..." subMessage="Please wait while we fetch your clients" />
      )}

      {/* Client cards grid */}
      {!loading && filteredApps.length > 0 && (
        <Box
          style={{ gridAutoRows: "160px" }}
          sx={{
            display: "grid",
            width: "100%",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "24px",
            "@media (max-width: 1400px)": { gridTemplateColumns: "repeat(3, 1fr)" },
            "@media (max-width: 1024px)": { gridTemplateColumns: "repeat(2, 1fr)" },
            "@media (max-width: 640px)":  { gridTemplateColumns: "repeat(1, 1fr)" },
          }}
        >
          {filteredApps.map((app) => {
            const agentCount = app.agents?.length || 0;
            const clientTypeLabel = app.client_type === "higher_ed" ? "Higher Education" : app.client_type === "ecommerce" ? "E-Commerce" : app.client_type || "Other";
            const agentTags = [...new Set((app.agents || []).map((a) => a.agent_type).filter(Boolean))];
            return (
              <EntityCard
                key={app.id || app.client_name}
                logoSrc={app.logo_url}
                title={app.client_name}
                subtitle={clientTypeLabel}
                footer={`${agentCount} ${agentCount === 1 ? "Agent" : "Agents"}`}
                tags={agentTags}
                onClick={() => handleAppClick(app)}
              />
            );
          })}
        </Box>
      )}

      {/* Empty state */}
      {!loading && filteredApps.length === 0 && (
        <Box sx={{ textAlign: "center", py: 10, width: "100%" }}>
          <Typography sx={{ fontSize: "2rem", mb: 1 }}>🏢</Typography>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#334155", mb: "6px" }}>
            {selectedFilter === "All" ? "No clients yet" : `No ${FILTERS.find((f) => f.value === selectedFilter)?.label} clients`}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
            {selectedFilter === "All"
              ? "Create your first client to get started"
              : `No clients found for the "${FILTERS.find((f) => f.value === selectedFilter)?.label}" filter`}
          </Typography>
        </Box>
      )}
    </>
  );
}
